import type { ArrangementStatus } from '../schema/arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHERE AN ORDER IS IN ITS OWN LIFE, AS A SCREEN SAYS IT. [C5b §3]
 *
 *  A client saw one order read `Partially Filled` on its status pill and `Filled: 19 of 19 · All
 *  Filled` on the strip beside it. Both were computed and both were "right": the pill was
 *  `ALLOCATION_IN_PROGRESS`, which `markWorkBegun` writes when the FIRST officer is named and
 *  nothing ever moves on — not at 19 of 19, not back at 0 of 19. It is a workflow marker, and
 *  C5 §9a had given it a FILL word. A lifecycle word that claims a fill fact contradicts the
 *  fill state beside it on every order that is not exactly half done.
 *
 *  ⚠️ SO THE REDUCTION IS: `ALLOCATION_IN_PROGRESS` READS AS `PUBLISHED` (decision 1, with the
 *  client). Fill words come from the derived fill state and from nothing else. The STORED status
 *  is untouched — `markWorkBegun`, the standing-order generator's "carrying work" test and every
 *  query on `status` keep the two apart, which is what they are for.
 *
 *  ⚠️ IT IS A WORD, NOT A FIFTH OFFICER STATE. `officerDutyState.constant.ts` already makes the
 *  same reduction for a constable (`CONFIRMED`); this is the dashboard's, and the two are kept
 *  apart for that file's reason — a nodal and a constable are told different true things.
 *
 *  ⚠️ NO WORDS AND NO TONE HERE — `officerDutyState.constant.ts`'s rule verbatim: the English
 *  lives in `web/src/lib/labels.ts`, because a label change must never be able to alter a
 *  contract value. What is shared is the REDUCTION, so the status filter and the pill cannot
 *  disagree about which statuses a word covers. The inverse, `statusesOfArrangementStatusWord`,
 *  is in `schema/arrangement.schema.ts` because deriving it needs the runtime status tuple.
 *
 *  ⚠️ TYPE-ONLY IMPORT, so this file can sit in `constants/` beside its officer twin and a
 *  client with a budget can read it without pulling zod.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_STATUS_WORDS = ['DRAFT', 'PUBLISHED', 'ACTIVE', 'CLOSED', 'CANCELLED'] as const;

export type ArrangementStatusWord = (typeof ARRANGEMENT_STATUS_WORDS)[number];

/** Total over the status enum; the compiler refuses a seventh status here until it has a word. */
export const arrangementStatusWordOf = (status: ArrangementStatus): ArrangementStatusWord => {
  switch (status) {
    /*
     * ⚠️ TWO STATUSES, ONE WORD — and the comment sits ABOVE both labels, for
     * `officerDutyStateOf`'s `no-fallthrough` reason.
     */
    case 'PUBLISHED':
    case 'ALLOCATION_IN_PROGRESS':
      return 'PUBLISHED';
    case 'DRAFT':
      return 'DRAFT';
    case 'ACTIVE':
      return 'ACTIVE';
    case 'CLOSED':
      return 'CLOSED';
    case 'CANCELLED':
      return 'CANCELLED';
  }
};
